const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}


function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    console.log(sectionId);
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

async onButtonClick(event) {
    event.preventDefault();

    const optionsList = document.querySelector('#sizeOptions');
    if (!optionsList) {
        console.error('optionsList element not found!');
        return;
    }

    const datasetMin = parseInt(this.input.dataset.min) || 1;
    let quantityInCart = 0;

    const variantId = document.querySelector('.button_variant:first-child')?.getAttribute('data-id');

    if (variantId) {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            quantityInCart = cart.items
                .filter(item => String(item.product_id) === String(variantId))
                .reduce((sum, item) => sum + item.quantity, 0);
        } catch (error) {
            console.error('Error fetching cart data:', error);
        }
    }

    const totalInventory = parseFloat(optionsList.dataset.inventory || "0");
    const maxInventory = totalInventory - quantityInCart;

    const selectedOption = parseFloat(document.querySelector('.active')?.dataset.varianttitle) || 1;

    const min = Math.max(1, datasetMin);
    let max = Math.floor(maxInventory / selectedOption);
    if (max < min) max = min;

    const previousValue = parseInt(this.input.value);

    if (event.target.name === 'plus') {
        this.input.value = Math.min(this.input.valueAsNumber + 1, max);
    } else {
        this.input.value = Math.max(this.input.valueAsNumber - 1, min);
    }

    if (previousValue !== parseInt(this.input.value)) {
        this.input.dispatchEvent(this.changeEvent);
    }

    await this.updateButtonState();
}

async updateButtonState() {
    const optionsList = document.querySelector('#sizeOptions');
    if (!optionsList) {
        console.error('optionsList element not found!');
        return;
    }

    const value = parseInt(this.input.value);
    const datasetMin = parseInt(this.input.dataset.min) || 1;
    let quantityInCart = 0;

    const variantId = document.querySelector('.button_variant:first-child')?.getAttribute('data-id');
    if (variantId) {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            quantityInCart = cart.items
                .filter(item => String(item.product_id) === String(variantId))
                .reduce((sum, item) => sum + item.quantity, 0);
        } catch (error) {
            console.error('Error fetching cart data:', error);
        }
    }

    const totalInventory = parseFloat(optionsList.dataset.inventory || "0");
    const maxInventory = totalInventory - quantityInCart;

    const selectedOption = parseFloat(document.querySelector('.active')?.dataset.varianttitle) || 1;

    const min = Math.max(1, datasetMin);
    let max = Math.floor(maxInventory / selectedOption);
    if (max < min) max = min;

    const errorEl = document.querySelector('#quantityError');

    // Disable buttons
    if (this.plusButton) this.plusButton.disabled = value >= max;
    if (this.minusButton) this.minusButton.disabled = value <= min;

    // Error handling
    if (errorEl) {
        if (max <= 0) {
            errorEl.textContent = 'Not enough stock for selected size';
            errorEl.hidden = false;
        } else if (value >= max) {
            errorEl.textContent = `Maximum available quantity is ${max}`;
            errorEl.hidden = false;
        } else {
            errorEl.hidden = true;
        }
    }
}






  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      // if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      // this.addEventListener('pointerup', (event) => {
      //   if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      // });
    } else {
      // this.addEventListener('click', (event) => {
      //   if (event.target === this) this.hide();
      // });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('#')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal')); 
      
      if (modal) modal.show(button);
    });
  }
}

customElements.define("modal-opener", ModalOpener);


class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    
    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;

    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
// header drawer
document.querySelector('.close_icon-dawer .icon-close').addEventListener('click', () => {
    document.querySelector('.header__icon--menu').click();
});

class TabSwitcher extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const buttons = this.querySelectorAll('[data-tab]');
    const contents = this.querySelectorAll('.tab-content');

    // Ensure only the first button and content are active on load
    buttons.forEach(btn => btn.classList.remove('active'));
    contents.forEach(ct => ct.classList.remove('active'));

    if (buttons.length && contents.length) {
      buttons[1].classList.add('active');
      const firstTab = buttons[1].dataset.tab;
      const firstContent = this.querySelector(`#${firstTab}`);
      if (firstContent) {
        firstContent.classList.add('active');
      }
    }

    // Add click event listeners
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        const tab = button.dataset.tab;
        ;
         if(e.target.getAttribute('data-tab') == 'style'){
          document.querySelector('#tabScroll').style.display = 'none';
          document.body.classList.add('loading')
        }else{
          document.querySelector('#tabScroll').style.display = 'block';
          document.body.classList.add('loading')
        }
        buttons.forEach(btn => btn.classList.remove('active'));
        contents.forEach(content => content.classList.remove('active'));

        button.classList.add('active');
         
        const activeContent = this.querySelector(`#${tab}`);
        if (activeContent) {
          activeContent.classList.add('active');
        }
         setTimeout(function(){
          document.body.classList.remove('loading');
          },1000)
      });
    });
  }
}
customElements.define('tab-switcher', TabSwitcher);

document.addEventListener('click', function(e) {
  if (e.target.closest('.back-button')) {
    const drawer = e.target.closest('.active .drawer-content');
    if (drawer && drawer.classList.contains('open')) {
      drawer.classList.remove('open');
      const wrapper = document.querySelector('.active .image-wrapper');
      wrapper.classList.forEach(cls => {
        if (cls.startsWith('active_')) {
          wrapper.classList.remove(cls);
        }
      });
    }
    
  }
});

document.addEventListener('DOMContentLoaded', function () {  
  if(document.body.classList.contains('collection-page')){    
    if(!document.body.classList.contains('page-customization-page')){
      sessionStorage.setItem("productTag",document.body.getAttribute('data-handle'));
      var get_tag = document.body.getAttribute('data-handle');
      var get_selected_size = parseFloat(sessionStorage.getItem("selected_size_value"));
      if(get_tag == 'suit') { 
        sessionStorage.setItem("multi_tag", 'suit,pant');
          sessionStorage.setItem("variant_size",'3.50');
      }else if(get_tag == 'pant') {
        sessionStorage.setItem("multi_tag", 'pant');
          sessionStorage.setItem("variant_size",'1.30');
        
      }else if(get_tag == '3-piece-suit') { 
        sessionStorage.setItem("multi_tag", 'suit,pant,koti'); 
        sessionStorage.setItem("productTag", '3-piece-suit');
          sessionStorage.setItem("variant_size",'4.30');
      } else if(get_tag == 'koti') { 
        sessionStorage.setItem("multi_tag", 'koti'); 
        sessionStorage.setItem("productTag", 'koti'); 
          sessionStorage.setItem("variant_size",'1.30');
      }else if(get_tag == 'indiankoti') { 
        sessionStorage.setItem("multi_tag", 'indiankoti'); 
        sessionStorage.setItem("productTag", 'indiankoti');
          sessionStorage.setItem("variant_size",'1.30');
      } else if(get_tag == 'jodhpuri') { 
        sessionStorage.setItem("multi_tag", 'jodhpuri,pant'); 
        sessionStorage.setItem("productTag", 'jodhpuri');
          sessionStorage.setItem("variant_size",'3.50');
      } else if(get_tag == 'blazer') { 
        sessionStorage.setItem("multi_tag", 'suit'); 
        sessionStorage.setItem("productTag", 'blazer'); 
          sessionStorage.setItem("variant_size",'2.30');
      } else if(get_tag == 'shirt') { 
        sessionStorage.setItem("multi_tag", 'shirt'); 
        sessionStorage.setItem("productTag", 'shirt'); 
          sessionStorage.setItem("variant_size",'1.70');
      } else if(get_tag == 'indowestern') { 
        sessionStorage.setItem("multi_tag", 'indowestern'); 
        sessionStorage.setItem("productTag", 'indowestern');
          sessionStorage.setItem("variant_size",'2.50');
      }else if(get_tag == 'kurta') { 
        sessionStorage.setItem("multi_tag", 'kurta'); 
        sessionStorage.setItem("productTag", 'kurta');
          sessionStorage.setItem("variant_size",'2.30');
      }else if(['jacket','jackets','shacket','shaket','scaket'].includes(String(get_tag||'').toLowerCase())) { 
        const jt = String(get_tag||'').toLowerCase();
        sessionStorage.setItem("multi_tag", jt); 
        sessionStorage.setItem("productTag", jt);
          sessionStorage.setItem("variant_size",'2.30');
      }
    }
  }
    if(document.body.classList.contains('product-page')){  
      setTimeout(function(){ 
        if(window.location.href.split('#view=')[1] != '' || window.location.href.includes('#view=') != false ){
            var get_tag = window.location.href.split('#view=')[1];
            if(get_tag != undefined ){
              sessionStorage.setItem('productTag',get_tag)
            }else{
               sessionStorage.setItem('productTag','')
            }
        } else{
          var get_tag = sessionStorage.getItem("productTag");
        }
        if(get_tag == 'suit') { 
        sessionStorage.setItem("multi_tag", 'suit,pant');
          sessionStorage.setItem("variant_size",'3.50');
      }else if(get_tag == 'pant') {
        sessionStorage.setItem("multi_tag", 'pant');
          sessionStorage.setItem("variant_size",'1.30');
        
      }else if(get_tag == '3-piece-suit') { 
        sessionStorage.setItem("multi_tag", 'suit,pant,koti'); 
        sessionStorage.setItem("productTag", '3-piece-suit');
          sessionStorage.setItem("variant_size",'4.30');
      } else if(get_tag == 'koti') { 
        sessionStorage.setItem("multi_tag", 'koti'); 
        sessionStorage.setItem("productTag", 'koti'); 
          sessionStorage.setItem("variant_size",'1.30');
      }else if(get_tag == 'indiankoti') { 
        sessionStorage.setItem("multi_tag", 'indiankoti'); 
        sessionStorage.setItem("productTag", 'indiankoti');
          sessionStorage.setItem("variant_size",'1.30');
      } else if(get_tag == 'jodhpuri') { 
        sessionStorage.setItem("multi_tag", 'jodhpuri,pant'); 
        sessionStorage.setItem("productTag", 'jodhpuri');
          sessionStorage.setItem("variant_size",'3.50');
      } else if(get_tag == 'blazer') { 
         sessionStorage.setItem("multi_tag", 'suit'); 
        sessionStorage.setItem("productTag", 'blazer'); 
          sessionStorage.setItem("variant_size",'2.30');
      } else if(get_tag == 'shirt') { 
        sessionStorage.setItem("multi_tag", 'shirt'); 
        sessionStorage.setItem("productTag", 'shirt'); 
          sessionStorage.setItem("variant_size",'1.70');
      } else if(get_tag == 'indowestern') { 
        sessionStorage.setItem("multi_tag", 'indowestern'); 
        sessionStorage.setItem("productTag", 'indowestern');
          sessionStorage.setItem("variant_size",'2.50');
      }else if(get_tag == 'kurta') { 
        sessionStorage.setItem("multi_tag", 'kurta'); 
        sessionStorage.setItem("productTag", 'kurta');
          sessionStorage.setItem("variant_size",'2.30');
      }else if(['jacket','jackets','shacket','shaket','scaket'].includes(String(get_tag||'').toLowerCase())) { 
        const jt = String(get_tag||'').toLowerCase();
        sessionStorage.setItem("multi_tag", jt); 
        sessionStorage.setItem("productTag", jt);
          sessionStorage.setItem("variant_size",'2.30');
      }
      },1500)
      if(window.location.href.split('#view=')[1] != ''){
          var tag = window.location.href.split('#view=')[1];
      } else{
        var tag = sessionStorage.getItem("productTag");
      }  
      if (tag && tag.trim() !== "") {    
      } else {    
        sessionStorage.setItem("productTag", "");
        if(document.body.classList.contains('product-page')){
          setTimeout(function(){
            document.querySelector('.selected-size').style.display = 'none';
            document.querySelector('.button_variant').classList.remove('active');
            document.querySelector('.customization_block').classList.add('disabled');
            document.querySelector('.product-form__buttons .product-form__submit').classList.add('disabled');
            document.querySelector('.product-form__buttons .product-form__submit').setAttribute('disabled','disabled');
        
          // Select the element
          const wrapper = document.querySelector('.size-header');

          if (wrapper) {
            // Create a new text node or element
            const textNode = document.createElement('span');
            textNode.classList.add('size_error') // You can also use 'p', 'div', etc.
            textNode.textContent = 'Please Select Size'; // Replace with your desired text

            // Insert it after the wrapper
            wrapper.insertAdjacentElement('afterend', textNode);
          }
          },500)
        }
      }
      /* PDP: drop all customization picks + preview blobs so a new fabric/product starts clean */
      (function clearCustomizationSessionStorageForPdp() {
        try {
          var keys = [];
          for (var i = 0; i < sessionStorage.length; i++) {
            var k = sessionStorage.key(i);
            if (!k) continue;
            if (
              k.indexOf('customSelections') === 0 ||
              k.indexOf('customPreviewImage_') === 0
            ) {
              keys.push(k);
            }
          }
          keys.forEach(function (key) {
            sessionStorage.removeItem(key);
          });
          sessionStorage.removeItem('fabricDescription');
        } catch (e) { /* ignore quota / private mode */ }
      })();

  }
  if(document.body.classList.contains('product-page')){  
    const sizeOptions = document.querySelector("#sizeOptions");
    const sizeOptions_wrp = document.querySelector(".size-selection-wrapper");
    const customization_block = document.querySelector(".customization_block");
    const customization_error = document.querySelector(".size_error");
    const addToCartBtn = document.querySelector(".product-form .product-form__submit");
  if (!sizeOptions) return;

  const totalInventory = parseFloat(sizeOptions.dataset.inventory);
  if (isNaN(totalInventory)) return;

  fetch("/cart.js")
    .then(res => res.json())
    .then(cart => {
        setTimeout(function(){
      let usedInventory = 0;
      const variant_title = document.querySelector("#sizeOptions .active").getAttribute('data-varianttitle');
      cart.items.forEach(item => {
        if (!item.variant_title) return;
        // Example: "5.00 mtr" → 5
        const variantValue = parseFloat(
          item.variant_title.replace(/[^0-9.]/g, "")
        );
        console.log(variant_title,'======',item.variant_title)
        if (!isNaN(variantValue) && item.variant_title == variant_title) {
          usedInventory += item.quantity;
        }
      });

      // If used inventory is greater or equal → disable
     
      if (usedInventory >= totalInventory) {
        const options = sizeOptions.querySelectorAll(".button_variant");
 
        options.forEach(option => {
          option.classList.add("disabled");
          option.setAttribute("aria-disabled", "true");

          if ("disabled" in option) {
            option.disabled = true;
          }
        });
        console.log(usedInventory,'usedInventory',totalInventory);
        if (addToCartBtn) {
          setTimeout(function(){
            addToCartBtn.disabled = true;
            addToCartBtn.classList.add("disabled");
            addToCartBtn.innerHTML = 'Sold Out';
            sizeOptions_wrp.classList.add("disabled");
            customization_block.classList.add("disabled");
            if(document.querySelector(".size_error") !== null){
              document.querySelector(".size_error").style.display = 'none';
            }
          },800)
        }
      }
        },1000)
    })
    .catch(err => console.error("Cart inventory check failed:", err));
  }
  let attached = false;
  let attempts = 0;
  const maxAttempts = 12;

function renderSelections() {
  const tagKeyMap = {
    pant: "customSelectionspant",
    suit: "customSelectionssuit",
    indiankoti: "customSelectionsindiankoti",
    koti: "customSelectionswestcoat",
    shirt: "customSelectionsshirt",
    indowestern: "customSelectionsindowestern",
    jodhpuri: "customSelectionsjodhpuri",
    kurta: "customSelectionskurta",
    jacket: "customSelectionsjacket"
  };

  // Update summary price
  const selections_price = sessionStorage.getItem("customVariantPrice");
  if (selections_price) {
    document.querySelectorAll('.summary-product-price')
      .forEach(priceEl => priceEl.textContent = selections_price);
  }

  // Get multi_tag array
  const multi_tag_str = (sessionStorage.getItem("multi_tag") || "").replace(/"/g, "");
  const multi_tag = multi_tag_str.split(",").map(t => t.trim()).filter(t => t !== "");
 const allDivs = document.querySelectorAll('.summary_page_inner');
  allDivs.forEach(div => {
      div.style.display = 'none'; // hide
  });
  if (!multi_tag.length) return;
  
  multi_tag.forEach(datatag => {
    const dtLower = (datatag || '').toLowerCase().trim();
    const jacketFamily = ['jacket', 'jackets', 'shacket', 'shaket', 'scaket'];
    const summaryDomTag = jacketFamily.includes(dtLower) ? 'jacket' : datatag;
    const storageKey = jacketFamily.includes(dtLower) ? 'customSelectionsjacket' : tagKeyMap[datatag];
    if (!storageKey) return;
    if(document.querySelector(`.summary_page_inner`) !== null){
      const sumEl = document.querySelector(`.summary_page_inner[data-tag="${summaryDomTag}"]`);
      if (sumEl) {
        sumEl.style.display = "flex";
        const tw = sumEl.querySelector('.summary-title-wrapper');
        if (tw) tw.innerHTML = datatag;
      }
    }
    const selections = sessionStorage.getItem(storageKey);
    if (!selections) return;

    const summaryContainer = document.querySelector(`.summary_page_inner[data-tag="${summaryDomTag}"] .summary-info-append`);
    if (!summaryContainer) return;
    summaryContainer.innerHTML = ""; // clear previous

try {
  const parsedSelections = JSON.parse(selections);
Object.keys(parsedSelections).forEach(title => {
  const { dataSrc, radioValue, baseImg, summary } = parsedSelections[title];

  /* ===============================
     CLEAN & PARSE SUMMARY
  =============================== */

  let summaryItems = [];

  if (typeof summary === "string") {
    summaryItems = summary
      .replace(/^\[/, "")        // remove leading [
      .replace(/\]$/, "")        // remove trailing ]
      .split("||")[0]            // remove || and anything after
      .split(",")                // split by comma
      .map(item => item.trim())
      .filter(
        item =>
          item &&
          item.toLowerCase() !== "undefined"
      );                          // ❌ remove empty & "undefined"
  }

  /* ===============================
     BUILD MEASUREMENT HTML
     (ONLY IF VALID DATA EXISTS)
  =============================== */

  const measurementHTML = summaryItems.length
    ? `
      <div class="measurement-points" data-json="${summaryItems}">
        <span class="measurement-title">Measurement Points:</span>
        ${summaryItems
          .map(item => `<span class="measurement-item">${item.replace(/inc/g, '"')}</span>`)
          .join("")}
      </div>
    `
    : "";

  /* ===============================
     BUILD MAIN BLOCK
  =============================== */

  const wrapper = document.createElement("div");
  wrapper.classList.add("summary-selected-block");

  wrapper.innerHTML = `
    <span class="summary-selected-title">${title.replace(/_/g, ' ')}</span>
    <div class="summary-selected-inner-info">
      <img
        src="${baseImg || dataSrc || ''}"
        alt="${radioValue || ''}"
        width="160"
      />
      <div class="inner-selecred-wrapper">
        <span class="selected-inner-value">${radioValue || ''}</span>
        ${measurementHTML}
      </div>
    </div>
  `;

  summaryContainer.appendChild(wrapper);
});


  /* ===============================
     RESTORE PREVIEW IMAGE
  =============================== */

  const savedPreview = sessionStorage.getItem(`customPreviewImage_${datatag}`);
  if (savedPreview) {
    const summaryImg = document.querySelector(
      `.summary_page_inner[data-tag="${datatag}"] .summary_image_canvas`
    );
    if (summaryImg) summaryImg.src = savedPreview;
  }

  /* ===============================
     SIZE / INVENTORY MESSAGE
  =============================== */

  const gt_selected_option = parseFloat(sessionStorage.getItem("selected_size_value")) || 0;
  const gt_total_option = parseFloat(sessionStorage.getItem("variant_size")) || 0;
  const tag = sessionStorage.getItem("productTag") || "";
  const selected_size = sessionStorage.getItem("selected_size_value") || "";

    if (gt_selected_option < gt_total_option) {
      const summaryWrapper = document.querySelector(".info_check");
      if (summaryWrapper) {

        // Prevent duplicate block
        if (!summaryWrapper.querySelector(".infor")) {
          const selectedSizeEl = document.createElement("div");
          selectedSizeEl.className = "infor";
          var get_availabelVariant = sessionStorage.getItem('availabelVariant');
          if (get_availabelVariant == 'false') {
            selectedSizeEl.innerHTML = `
              <span>You have selected <strong>${selected_size} meters for ${tag}</strong></span>
              <input type="checkbox" id="accepted_id" class="checkbox_check">
              <label for="accepted_id">
                <span>
                  ${tag} will require <strong>${gt_total_option} meters</strong>, which is not currently available.
                  Please confirm by checking the checkbox.
                </span>
              </label>
              <input type="checkbox" id="accepted_id_2" class="checkbox_check">
              <label for="accepted_id_2">
                <span>
                We are selling Fabrics Only
                </span>
              </label>
            `;
          } else {
            selectedSizeEl.innerHTML = `
            <span>You have selected <strong>${selected_size} meters for ${tag}</strong></span>
              <input type="checkbox" id="accepted_id" class="checkbox_check preselected">
              <label for="accepted_id">
                <span>
                  ${tag} will require <strong>${gt_total_option} meters</strong>.
                  Once the fabric is cut, it cannot be exchanged or returned.
                  Please check your size from
                  <a href="/pages/size-chart" target="_blank">this link</a>.
                  </span>
              </label>
              <input type="checkbox" id="accepted_id_2" class="checkbox_check">
              <label for="accepted_id_2">
                <span>
                We are selling Fabrics Only
                </span>
              </label>
            `;
          }

          summaryWrapper.prepend(selectedSizeEl);
        }
      }
    }else{
      const summaryWrapper = document.querySelector(".info_check");
      if (!summaryWrapper.querySelector(".infor")) {
          const selectedSizeEl = document.createElement("div");
          selectedSizeEl.className = "infor";
          selectedSizeEl.innerHTML = `
          <span>You have selected <strong>${selected_size} meters for ${tag}</strong></span>
            <input type="checkbox" id="accepted_id" class="checkbox_check">
            <label for="accepted_id">
              <span>
                Once the fabric is cut, it cannot be exchanged or returned.
                Please check your size from
                <a href="/pages/size-chart" target="_blank">this link</a>.
              </span>
            </label>
            <input type="checkbox" id="accepted_id_2" class="checkbox_check">
              <label for="accepted_id_2">
                <span>
                We are selling Fabrics Only
                </span>
              </label>
          `;
          summaryWrapper.prepend(selectedSizeEl);
      }

    }

    const variantWrapper = document.querySelector(".summary-size-selection-wrapper .size-selection-content");
    const variantHeader = document.querySelector(".summary-size-selection-wrapper .size-header");

    if (variantWrapper.dataset.rendered === "true") return;
    variantWrapper.dataset.rendered = "true";

    const productHandle = sessionStorage.getItem("customProductHandle");
    const sizeList = variantWrapper.querySelector("ul");
    const get_total_inventory = sessionStorage.getItem("Total");

    sizeList.className = "size-options-list sizeOptions_active";
    sizeList.id = "sizeOptions";
    sizeList.dataset.sizeOption = "";
    sizeList.setAttribute("data-inventory", get_total_inventory);
    sizeList.innerHTML = "";

    function adjustOptions(dropdownMenu, plusBtn, optionsList, container, remainingInventory) {
      dropdownMenu.innerHTML = "";
      const containerWidth = container.offsetWidth;
      let usedWidth = plusBtn.offsetWidth + 10;
      const children = [...optionsList.querySelectorAll(".size-option:not(.plus-btn)")];

      children.forEach(child => {
        const sizeValue = parseFloat(child.dataset.varianttitle || "0");
        optionsList.classList.add("sizeOptions_active");
        if (remainingInventory <= 0) {
          child.style.display = "inline-block";
          child.classList.add("disabled-option");
        } else {
          child.style.display = sizeValue > remainingInventory ? "none" : "inline-block";
          child.classList.remove("disabled-option");
        }
      });

      children.forEach(child => {
        if (child.style.display === "none") return;
        const childWidth = child.offsetWidth + 10;
        if (usedWidth + childWidth > containerWidth) {
          child.style.display = "none";
          const li = document.createElement("li");
          li.className = "dropdown-item button_variant";
          li.textContent = child.textContent;
          ["variant", "price", "varianttitle", "availabel", "id"].forEach(attr => {
            if (child.dataset[attr]) li.dataset[attr] = child.dataset[attr];
          });
          dropdownMenu.appendChild(li);
        } else {
          usedWidth += childWidth;
        }
      });

      const infoCheck = document.querySelector("product-info .info_check");
      const addButton = document.querySelector(".submit-customization");

      if (remainingInventory <= 0) {
        plusBtn.classList.add("disabled-option");
        if (infoCheck) {
          infoCheck.style.display = "none";
        }
        if (addButton) {
          const textNode = Array.from(addButton.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "");
          if (textNode) {
            textNode.textContent = "SOLD OUT";
          }
          addButton.classList.add("disabled");
        }
      } else {
        plusBtn.classList.remove("disabled-option");
        if (infoCheck) {
          infoCheck.style.display = "block";
        }
        if (addButton) {
          const textNode = Array.from(addButton.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== "");
          if (textNode) {
            textNode.textContent = "ADD TO CART";
          }
          addButton.classList.remove("disabled");
        }
      }

      plusBtn.style.display = dropdownMenu.children.length ? "inline-block" : "none";
    }

    function reorderOptionsKeepSelectedFirst(selectedOption, optionsList, remainingInventory) {
      const originalOrder = JSON.parse(optionsList.dataset.originalOrder || "[]");
      const selectedVariant = selectedOption.dataset.variant;
      const sizeOptions = [...optionsList.querySelectorAll(".size-option:not(.plus-btn)")];
      const optionMap = {};
      sizeOptions.forEach(opt => (optionMap[opt.dataset.variant] = opt));
      sizeOptions.forEach(opt => opt.remove());
      const plusBtn = optionsList.querySelector(".plus-btn");
      if (optionMap[selectedVariant]) {
        optionsList.insertBefore(optionMap[selectedVariant], plusBtn);
      }
      originalOrder.forEach(id => {
        if (id !== selectedVariant && optionMap[id]) {
          const size = parseFloat(optionMap[id].dataset.varianttitle || "0");
          if (size <= remainingInventory) {
            optionsList.insertBefore(optionMap[id], plusBtn);
          }
        }
      });
    }

    function handleSizeSelection(target, root, remainingInventory) {
      const optionsList = root.querySelector("[data-size-option]");
      const dropdownMenu = root.querySelector("[data-dropdown-menu]");
      const plusBtn = root.querySelector("[data-dropdown-toogle]");
      const container = root.querySelector(".size-selection-content");
      const variantId = target.dataset.variant;
      const price = target.dataset.price;
      const variantTitle = target.dataset.varianttitle;
      const available = target.dataset.availabel;

      root.querySelectorAll(".size-option, .dropdown-item")
        .forEach(el => el.classList.remove("active"));
        const selectedSize = root.querySelector("#selected-size") || document.querySelector("#selected-size");
        if (selectedSize) {
          selectedSize.textContent = target.textContent;
          selectedSize.style.display = "contents";
        }
        const customizeBtn = root.querySelector(".customization_block");
        const addBtn = document.querySelector(".product-form__submit");
        if (available == "false" || remainingInventory <= 0) {
          customizeBtn?.classList.add("disabled");
          addBtn?.setAttribute("disabled", true);
          document.querySelector(".notify_btn")?.style.setProperty("display", "flex");
        } else {
          customizeBtn?.classList.remove("disabled");
          addBtn?.removeAttribute("disabled");
          document.querySelector(".notify_btn")?.style.setProperty("display", "none");
        }
        console.log(variantId,"variantId Handlesize selection")
        sessionStorage.setItem("customVariantId", variantId);
        sessionStorage.setItem("customVariantPrice", price);
        sessionStorage.setItem("selected_size_value", target.textContent);
        const variantInput = root.querySelector(".product-variant-id");
        if (variantInput) variantInput.value = variantId;
        const priceEl = document.querySelector(".summary-product-price");
        if (priceEl && price) {
          priceEl.textContent = price;
        }
        updateInfoCheck();
        document.dispatchEvent(new Event("variant:changed"));
        let newOption = target.classList.contains("dropdown-item")
          ? optionsList.querySelector(`.size-option[data-variant="${variantId}"]`)
          : target;

        newOption?.classList.add("active");
        reorderOptionsKeepSelectedFirst(newOption, optionsList, remainingInventory);
        adjustOptions(dropdownMenu, plusBtn, optionsList, container, remainingInventory);
    }

    function initCustomSize(root) {
      const container = root.querySelector(".size-selection-content");
      const optionsList = root.querySelector("[data-size-option]");
      const plusBtn = root.querySelector("[data-dropdown-toogle]");
      const dropdownMenu = root.querySelector("[data-dropdown-menu]");
      if (!container || !optionsList || !plusBtn || !dropdownMenu) return;
      let remainingInventory = 0;

      if (!optionsList.dataset.originalOrder) {
        optionsList.dataset.originalOrder = JSON.stringify(
          [...optionsList.querySelectorAll(".size-option:not(.plus-btn)")].map(el => el.dataset.variant)
        );
      }

      async function updateOptions() {
        let usedInventory = 0;
        const first = optionsList.querySelector(".size-option");
        const productId = first?.dataset.id;

        if (productId) {
          const cart = await fetch("/cart.js").then(r => r.json());
          cart.items
            .filter(i => String(i.product_id) === String(productId))
            .forEach(i => {
              const size = parseFloat(i.variant_title.replace("mtr", "") || "0");
              usedInventory += i.quantity * size;
            });
        }

        const totalInventory = parseFloat(optionsList.dataset.inventory || "0");
        remainingInventory = Math.max(totalInventory - usedInventory, 0);
        adjustOptions(dropdownMenu, plusBtn, optionsList, container, remainingInventory);
        const activeOption = optionsList.querySelector(".size-option.active:not(.disabled-option)");

        if (activeOption) {
          reorderOptionsKeepSelectedFirst(activeOption,optionsList,remainingInventory);
          adjustOptions(dropdownMenu,plusBtn,optionsList,container,remainingInventory);
        }
      }

      updateOptions();

      window.addEventListener("resize", () =>
        adjustOptions(dropdownMenu, plusBtn, optionsList, container, remainingInventory)
      );

      plusBtn.addEventListener("click", e => {
        e.stopPropagation();
        dropdownMenu.style.display =
          dropdownMenu.style.display === "block" ? "none" : "block";
      });

      optionsList.addEventListener("click", e => {
        const option = e.target.closest(".size-option:not(.disabled-option)");
        if (option) handleSizeSelection(option, root, remainingInventory);
      });

      dropdownMenu.addEventListener("click", e => {
        const item = e.target.closest(".dropdown-item");
        if (item) handleSizeSelection(item, root, remainingInventory);
        dropdownMenu.style.display = "none";
      });

      document.addEventListener("click", e => {
        if (!plusBtn.contains(e.target) && !dropdownMenu.contains(e.target)) {
          dropdownMenu.style.display = "none";
        }
      });
    }

    function updateInfoCheck() {
      const gt_selected_option = parseFloat(sessionStorage.getItem("selected_size_value")) || 0;
      const gt_total_option = parseFloat(sessionStorage.getItem("variant_size")) || 0;
      const tag = sessionStorage.getItem("productTag") || "";
      const selected_size = sessionStorage.getItem("selected_size_value") || "";
      const availableVariant = sessionStorage.getItem("availabelVariant");

      const summaryWrapper = document.querySelector(".info_check");
      if (!summaryWrapper) return;
      let infoEl = summaryWrapper.querySelector(".infor");
      if (!infoEl) {
        infoEl = document.createElement("div");
        infoEl.className = "infor";
        summaryWrapper.prepend(infoEl);
      }

      if (gt_selected_option < gt_total_option) {
        if (availableVariant === "false") {
          infoEl.innerHTML = `
            <span>You have selected <strong>${selected_size} meters for ${tag}</strong></span>
            <input type="checkbox" id="accepted_id" class="checkbox_check">
            <label for="accepted_id">
              <span>
                ${tag} will require <strong>${gt_total_option} meters</strong>, which is not currently available.
                Please confirm by checking the checkbox.
              </span>
            </label>
            <input type="checkbox" id="accepted_id_2" class="checkbox_check">
            <label for="accepted_id_2">
              <span>We are selling Fabrics Only</span>
            </label>
          `;
        } else {
          infoEl.innerHTML = `
            <span>You have selected <strong>${selected_size} meters for ${tag}</strong></span>
            <input type="checkbox" id="accepted_id" class="checkbox_check preselected" checked>
            <label for="accepted_id">
              <span>
                ${tag} will require <strong>${gt_total_option} meters</strong>.
                Once the fabric is cut, it cannot be exchanged or returned.
                Please check your size from
                <a href="/pages/size-chart" target="_blank">this link</a>.
              </span>
            </label>
            <input type="checkbox" id="accepted_id_2" class="checkbox_check">
            <label for="accepted_id_2">
              <span>We are selling Fabrics Only</span>
            </label>
          `;
        }
      } else {
        infoEl.innerHTML = `
          <span>You have selected <strong>${selected_size} meters for ${tag}</strong></span>
          <input type="checkbox" id="accepted_id" class="checkbox_check" checked>
          <label for="accepted_id">
            <span>
              Once the fabric is cut, it cannot be exchanged or returned.
              Please check your size from
              <a href="/pages/size-chart" target="_blank">this link</a>.
            </span>
          </label>
          <input type="checkbox" id="accepted_id_2" class="checkbox_check">
          <label for="accepted_id_2">
            <span>We are selling Fabrics Only</span>
          </label>
        `;
      }
    }

    sizeList.querySelectorAll(".plus-btn").forEach(btn => btn.remove());

    fetch(`/products/${productHandle}.js`)
      .then(res => res.json())
      .then(product => {
        if (!product?.variants?.length) return;
        const originalOrder = product.variants.map(v => v.id.toString());
        sizeList.dataset.originalOrder = JSON.stringify(originalOrder);
        const variants = product.variants
          .map(v => {
            const sizeValue = parseFloat(v.title);
            return {
              id: v.id.toString(),
              title: v.title.replace(" mtr", ""),
              size: sizeValue,
              price: v.price,
              available: v.available
            };
          })
          .filter(v => !isNaN(v.size));

        if (!variants.length) return;
        variants.sort(
          (a, b) =>
            originalOrder.indexOf(a.id) - originalOrder.indexOf(b.id)
        );

        let selectedVariantId =
          sessionStorage.getItem("customVariantId") || variants[0].id;

        let selectedSize =
          parseFloat(sessionStorage.getItem("selected_size_value")) ||
          variants[0].size;

        let selectedPrice =
          sessionStorage.getItem("customVariantPrice") ||
          Shopify.formatMoney(variants[0].price, window.Money_Filter)
            .replace(".00", "")
            .replace(",00", "");

        sessionStorage.setItem("customVariantId", selectedVariantId);
        sessionStorage.setItem("selected_size_value", selectedSize);
        sessionStorage.setItem("customVariantPrice", selectedPrice);

        variantHeader.innerHTML = `
          <div class="variant-header">
            <div class="variant-title">
              SELECT SIZE:
              <span id="selected-size" style="display: contents;">${selectedSize} MTR</span>
            </div>
          </div>
        `;

        variants.forEach(v => {
          const li = document.createElement("li");
          li.className = "size-option button_variant dropdown-item";
          li.dataset.variant = v.id;
          li.dataset.id = product.id;
          li.dataset.varianttitle = v.title;
          li.dataset.availabel = v.available;
          li.dataset.price = Shopify.formatMoney(v.price,window.Money_Filter).replace(".00", "").replace(",00", "");
          li.textContent = `${v.title} mtr`;

          if (v.id === selectedVariantId) {
            li.classList.add("active");
          }

          li.addEventListener("click", () => {
            sizeList.querySelectorAll(".size-option").forEach(el => el.classList.remove("active"));
            li.classList.add("active");
            sessionStorage.setItem("customVariantId", v.id);
            sessionStorage.setItem("selected_size_value", v.size);
            sessionStorage.setItem("customVariantPrice", li.dataset.price);
            const sizeEl = document.getElementById("selected-size");
            if (sizeEl) sizeEl.textContent = v.size;
            const priceEl = document.querySelector(".summary-product-price");
            if (priceEl) priceEl.textContent = li.dataset.price;
            updateInfoCheck();
            document.dispatchEvent(new Event("variant:changed"));
          });
          sizeList.appendChild(li);
        });

        let plusBtn = sizeList.querySelector(".plus-btn");
        if (!plusBtn) {
          const plusBtn = document.createElement("li");
          plusBtn.className = "plus-btn";
          plusBtn.id = "dropdownToggle";
          plusBtn.textContent = "+";
          plusBtn.setAttribute("data-dropdown-toogle", "");
          sizeList.appendChild(plusBtn);
        }
        initCustomSize(document.querySelector("summary-product-info"));
        const activeOption = sizeList.querySelector(".size-option.active");
        if (activeOption) {
          reorderOptionsKeepSelectedFirst(
            activeOption,
            sizeList,
            parseFloat(sizeList.dataset.inventory || "0")
          );
        }
      })
      .catch(err => console.error("Variant fetch error:", err));



  } catch (err) {
    console.error("❌ Failed to parse", storageKey, err);
  }

  });
}
  const attachClick = () => {
    if (attached) return true;

    const savedId = sessionStorage.getItem("customVariantId");
    if (savedId) {
      console.log(savedId, 'savedId');
      const btn = document.querySelector('.product-position-data_button');
      if (btn) {
        btn.setAttribute("data-id", savedId);
      } else {
        console.warn('No .product-position-data_button found on the page.');
      }
    } else {
      console.warn('No customVariantId found in localStorage.');
    }

    const savedPrice = sessionStorage.getItem("customVariantPrice");
    if (savedPrice) {
        setTimeout(function(){
          const priceEls = document.querySelectorAll('.updatedprice');
          priceEls.forEach(el => {
            el.textContent = savedPrice;
          });
        },1200)
      }

document.body.addEventListener('click', async function (e) {
  if (!e.target.closest('.product-position-data_button')) return;

  const multi_tag_str = (sessionStorage.getItem("multi_tag") || "").replace(/"/g, "");
  const multi_tag = multi_tag_str.split(",").filter(Boolean);

  if (!multi_tag.length) {
    alert("No active tags found.");
    return;
  }

  const MAX_PIXELS = 16777216; // Safari safe limit

  const loadImage = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });

  for (const tag of multi_tag) {
    try {
      const images = document.querySelectorAll(
        `.product-details-wrapper[data-tag="${tag}"] .image-wrapper .image_position`
      );

      const validImages = Array.from(images)
        .filter(img => img.dataset.title !== "back")
        .filter(img => window.getComputedStyle(img).display !== "none")
        .map(img => img.currentSrc || img.src)
        .filter(Boolean);

      if (!validImages.length) continue;

      const loadedImages = (await Promise.all(
        validImages.map(src => loadImage(src))
      )).filter(Boolean);

      if (!loadedImages.length) continue;

      const targetWidth = 1640;
      const targetHeight = 2025;
      const dpr = window.devicePixelRatio || 1;

      // Safe scale for Safari
      const safeScale = Math.min(
        dpr,
        Math.sqrt(MAX_PIXELS / (targetWidth * targetHeight))
      );

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = Math.floor(targetWidth * safeScale);
      canvas.height = Math.floor(targetHeight * safeScale);

      ctx.scale(safeScale, safeScale);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // White background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, targetWidth, targetHeight);

      // Draw images
      for (const img of loadedImages) {
        ctx.drawImage(
          img,
          0,
          0,
          img.naturalWidth,
          img.naturalHeight,
          0,
          0,
          targetWidth,
          targetHeight
        );
      }

      // Export (JPEG for Safari compatibility)
      let quality = 0.85;
      let blob = await new Promise(resolve =>
        canvas.toBlob(resolve, "image/jpeg", quality)
      );

      // Safari fallback if toBlob returns null
      if (!blob) {
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const byteString = atob(dataUrl.split(',')[1]);
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }

        blob = new Blob([ab], { type: mimeString });
      }

      if (!blob) continue;

      // Compress if larger than 80KB
      while (blob.size / 1024 > 80 && quality > 0.4) {
        quality -= 0.05;
        blob = await new Promise(resolve =>
          canvas.toBlob(resolve, "image/jpeg", quality)
        );
        if (!blob) break;
      }

      if (!blob) continue;

      // Await FileReader (IMPORTANT FIX)
      await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => {
          sessionStorage.setItem(`customPreviewImage_${tag}`, reader.result);
          resolve();
        };
        reader.readAsDataURL(blob);
      });

      // Free Safari memory immediately
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;
      canvas.remove();

    } catch (err) {
      console.error(`Failed for tag: ${tag}`, err);
    }
  }

  renderSelections();

  // Redirect AFTER everything is done
  window.location.href = `/pages/customization-overview`;
});




    attached = true;
    return true;
  };

  const interval = setInterval(() => {
    attempts++;
    if (attachClick() || attempts >= maxAttempts) {
      clearInterval(interval);
      if (!attached) {
        console.warn("⏹️ Stopped checking after 12 attempts. Button not found.");
      }
    }
  }, 200);


  // ✅ restore preview image if saved
  const multi_tag_str = (sessionStorage.getItem("multi_tag") || "").replace(/"/g, "");
  const multi_tag = multi_tag_str.split(",").map(t => t.trim()).filter(t => t !== "");

  multi_tag.forEach(tag => {
    const savedPreview = sessionStorage.getItem(`customPreviewImage_${tag}`);
    if (savedPreview) {
      // Find the corresponding image container for this tag
      const summaryImg = document.querySelector(`.summary_page_inner[data-tag="${tag}"] .summary_image_canvas`);
      if (summaryImg) {
        summaryImg.src = savedPreview;
      }
    }
  });
  renderSelections();
});

document.addEventListener('DOMContentLoaded', function () {
  const submitBtn = document.querySelector('.submit-customization');
  if (!submitBtn) return;

  submitBtn.addEventListener('click', async function (e) {
    if(document.querySelector('.checkbox_check') !== null){

      if(!document.querySelector('#accepted_id').checked || !document.querySelector('#accepted_id_2').checked){
        if(document.querySelector('.customization-info') !== null){
          document.querySelector('.customization-info').remove();
        }
        const submitBtn = document.querySelector('.submit-customization');
        if (submitBtn) {
          // Create a new div or span for your text
          const infoText = document.createElement('div');
          infoText.className = 'customization-info';
          infoText.textContent = 'Please check the checkbox';

          // Insert it **above** the button
          submitBtn.parentNode.insertBefore(infoText, submitBtn);
        }
        return;
      }
    }


    const checkedEl = document.querySelector('#accepted_id:checked');
    const preselectedVariant = sessionStorage.getItem("PreselectedOption");
    let variantId;

    if (checkedEl && checkedEl.classList.contains('preselected') && preselectedVariant) {
      variantId = sessionStorage.getItem("customVariantId");
      console.log(variantId, "PreselectedOption");
    } else {
      variantId = sessionStorage.getItem("customVariantId");
      console.log(variantId, "variantId");
    }

    if(submitBtn.querySelector(".loading__spinner")) {
      submitBtn.querySelector(".loading__spinner").classList.remove("hidden");
      submitBtn.classList.add("loading");
    }
    if (!variantId) {
      alert("❌ No variant selected!");
      return;
    }

    // Get all active tags
    const multi_tag_str = (sessionStorage.getItem("multi_tag") || "").replace(/"/g, "");
    const multi_tag = multi_tag_str.split(",").map(t => t.trim()).filter(t => t !== "");

    if (!multi_tag.length) {
      alert("❌ No active tags found!");
      return;
    }

    // Convert base64 → File
function base64ToFile(base64, filename = "order-preview.jpg") {
  const [meta, data] = base64.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const binary = atob(data);
  const array = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }

  return new File([array], filename, { type: mime });
}

// Upload image and return API response
async function uploadPreviewImage(file) {
  const formData = new FormData();
  formData.append("image", file);

  const response = await fetch(
    "https://api.asapfabric.com/api/v1/upload/order-preview",
    {
      method: "POST",
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Image upload failed");
  }

  return await response.json();
}

// Main function
async function processPreviewImages(multi_tag) {
  const properties = {};
 
  for (const tag of multi_tag) {
   
      const base64Image = sessionStorage.getItem(`customPreviewImage_${tag}`);
      if (!base64Image) continue;

      try {
        const file = base64ToFile(base64Image);
        const uploadResult = await uploadPreviewImage(file);

        // Adjust key according to API response
        properties[`_Preview Image (${tag})`] = uploadResult.data.url || uploadResult;
      } catch (error) {
        console.error(`Upload failed for tag: ${tag}`, error);
      }
  }

    // Loop through all summary items
    document.querySelectorAll('.summary-selected-inner-info').forEach((item, index) => {
      const img = item.querySelector('img');
      const valueEl = item.querySelector('.selected-inner-value');
      const measurement_content = item.querySelector('.measurement-points')?.getAttribute('data-json') || '{}';
      const tag = item.closest('.summary_page_inner').getAttribute('data-tag');
      const title = item.closest('.summary-selected-block').querySelector('.summary-selected-title').innerHTML.trim();
      if (!img || !valueEl) return;
      properties[`_${tag}_${title}_image`] = img.getAttribute('src')
      properties[`_${tag}_${title}`] = valueEl.innerHTML.trim() + ' - ' +  measurement_content;
    });


  return properties;
}

// ▶️ Usage
const properties = await processPreviewImages(multi_tag);
console.log("Final properties:", properties);

    if (Object.keys(properties).length === 0) {
      alert("❌ No preview images available!");
      return;
    }
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
          properties: properties
        })
      });

      if (response.ok) {
        if(submitBtn.querySelector(".loading__spinner")) {
          submitBtn.querySelector(".loading__spinner").classList.add("hidden");
          submitBtn.classList.remove("loading");
        }
        window.location.href = '/cart';
      } else {
        const errorData = await response.json();
        console.error("Failed to add product:", errorData);
        alert("❌ Failed to add product to cart.");
      }
    } catch (err) {
      console.error("Add to cart error:", err);
      alert("❌ Something went wrong. See console.");
    }
  });
});

class ProductSwatchToggle extends HTMLElement {
  connectedCallback() {
    const swatch = this.querySelector(".product_swatch_wrapper");
    const targetId = this.getAttribute("id");
    if (!targetId || !swatch) return;

    
      const button = document.querySelector(`.card_customize_button[data-target="${targetId}"]`);
      if (button) {
        button.addEventListener("click", () => {
          swatch.style.display = "flex";
        });
      }
    
  }
}
customElements.define("custom-swatch", ProductSwatchToggle);

document.addEventListener('click', function (e) {
 
  const backBtn = e.target.closest('.back-button');
  if (!backBtn) return;
  if(backBtn){
   document.body.classList.add('loading');
   }

  document
    .querySelector('.product-details-wrapper.active .tab-button[data-tab="style"]')
    ?.click();
   setTimeout(function(){
   document.body.classList.remove('loading');
   },1000)
});

window.addEventListener('load', () => {
  document.addEventListener('input', function (e) {
    if (!e.target.matches('.search-box input')) return;

    document.body.classList.remove('loading');

    const searchValue = e.target.value.trim().toLowerCase();
    const products = document.querySelectorAll('.product-listing-set');
    const noProductsMsg = document.querySelector('.active .product-featured-wrapper .no-products');

    // 🔹 If no products exist, stop
    if (!products.length) return;

    // 🔹 Empty search → show all
    if (searchValue === '') {
      products.forEach(product => {
        product.style.display = '';
      });
      noProductsMsg.style.display = 'none';
      return;
    }

    // 🔹 Filter products
    products.forEach(product => {
      const title =
        product.querySelector('.product-list-title')?.textContent.toLowerCase() || '';

      product.style.display = title.includes(searchValue) ? '' : 'none';
    });

    // 🔹 Count only actually visible grids
    const visibleCount = [...products].filter(
      product => product.offsetParent !== null
    ).length;

    console.log('Visible grids:', visibleCount,noProductsMsg);

    // 🔹 Show / hide "No products"
    noProductsMsg.style.display = visibleCount === 0 ? 'block' : 'none';
  });
});
 if ((sessionStorage.getItem("productTag") === null || sessionStorage.getItem("productTag") === '') && document.body.classList.contains('page-customize-overview-page')) {
   window.location.href = `/collections`;   
}